/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 8th 2022, 9:06:28 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { pick } from "lodash";
import { useNavigate } from "react-router-dom";

import { resolveURLName } from "core/routes/utils";
import { TextFormInput, OSPopUpAPIForm, DropDownFormInput } from "ui-widgets/forms";
import { OSActionButton, useToggleState } from "ui-widgets";
import { useWorkflowContext } from "service-builder/providers";
import { SUPPORTED_AUTOMATED_TASK_TYPES_WIDGETS } from "service-builder/components/workflow-builder/TaskTypeWidgets";
import { ActionDialog } from "ui-widgets/dialogs/GenericDialogs";
import { getDefaultTaskTemplate } from "lib/factories";
import { TaskTemplate, NODE_TYPES } from "lib/schemas";
import { useTaskTemplateAPIForm } from "service-builder/services";
import { useServiceBuilderContext } from "service-builder/providers";

const BILLING_NODE_TYPES = pick(NODE_TYPES, ["View", "Handler"]);

export const NewBillingTaskTemplateForm = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
  const {
    billingIcon,
    useTaskTemplateAPIFormReturn,
    successDialogOpenState,
    navigateToBillingConfigurationFn,
    navigateToWorkflowBuilderFn,
  } = useNewBillingTaskTemplateFormLogic(handleClose);

  return (
    <>
      <SuccessDialog
        successDialogOpenState={successDialogOpenState}
        navigateToBillingConfigurationFn={navigateToBillingConfigurationFn}
        navigateToWorkflowBuilderFn={navigateToWorkflowBuilderFn}
      />

      <OSPopUpAPIForm
        popUpTitle="New Billing Task Configuration"
        popUpIcon={billingIcon}
        open={open}
        onClose={handleClose}
        useAPIFormReturn={useTaskTemplateAPIFormReturn}
      >
        <DropDownFormInput<TaskTemplate>
          schema_field_key="type"
          label="Task Type"
          options={BILLING_NODE_TYPES}
          helperText="Will the bill be generated by a worker or automated?"
        />
        <TextFormInput<TaskTemplate>
          schema_field_key="title"
          label="Task Title"
          helperText="The title of the task that will generate a bill in the workflow."
        />
      </OSPopUpAPIForm>
    </>
  );
};

const useNewBillingTaskTemplateFormLogic = (handleClose: () => void) => {
  const {
    taskTemplateListContext: { updateList, endOfListPosition, selectItem },
  } = useWorkflowContext();

  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const { open: successDialogOpenState, handleOpen: openSuccessDialogFn } = useToggleState();

  const [createdBillingTaskTemplate, setCreatedBillingTaskTemplate] = React.useState<TaskTemplate>();

  const onSaveBillingTaskTemplate = (createdBillingTaskTemplate: TaskTemplate) => {
    updateList(createdBillingTaskTemplate);
    handleClose();
    setCreatedBillingTaskTemplate(createdBillingTaskTemplate);
    openSuccessDialogFn();
  };

  const navigate = useNavigate();

  const navigateToWorkflowBuilderFn = () => {
    if (!createdBillingTaskTemplate?.id) return;

    selectItem(createdBillingTaskTemplate.id);
    navigate(resolveURLName("serviceBuilderRoot", { serviceId: currentProcessDefinition.id }));
  };

  const navigateToBillingConfigurationFn = () => {
    if (!createdBillingTaskTemplate?.billing_configuration) return;

    navigate(
      resolveURLName("billingConfiguration", {
        serviceId: currentProcessDefinition.id,
        billingConfigurationId: createdBillingTaskTemplate.billing_configuration,
      })
    );
  };

  const newBillingTaskTemplateDefaultValues = getDefaultTaskTemplate({
    processDefinition: currentProcessDefinition,
    bpmnNodeType: null, // User must explicitly choose to ensure they are aware of what they are doing
    defaultSequence: endOfListPosition,
    title: "",
  });

  const useTaskTemplateAPIFormReturn = useTaskTemplateAPIForm(
    newBillingTaskTemplateDefaultValues?.id,
    newBillingTaskTemplateDefaultValues,
    onSaveBillingTaskTemplate
  );

  const {
    useFormReturn: { watch, reset },
  } = useTaskTemplateAPIFormReturn;

  const wactchedTaskType = watch<keyof TaskTemplate>("type") as keyof typeof BILLING_NODE_TYPES;

  React.useEffect(() => {
    if (wactchedTaskType === "View") {
      reset({ ...newBillingTaskTemplateDefaultValues, type: wactchedTaskType, user_action_type: "billing" });
    }

    if (wactchedTaskType === "Handler") {
      reset({ ...newBillingTaskTemplateDefaultValues, type: wactchedTaskType, automated_task_type: "auto_billing" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wactchedTaskType]);

  const {
    auto_billing: { automatedTaskTypeIcon: billingIcon },
  } = SUPPORTED_AUTOMATED_TASK_TYPES_WIDGETS;

  return {
    billingIcon,
    useTaskTemplateAPIFormReturn,
    successDialogOpenState,
    navigateToWorkflowBuilderFn,
    navigateToBillingConfigurationFn,
  };
};

interface SuccessDialogProps {
  successDialogOpenState: boolean;
  navigateToWorkflowBuilderFn: () => void;
  navigateToBillingConfigurationFn: () => void;
}

const SuccessDialog = ({
  successDialogOpenState,
  navigateToWorkflowBuilderFn,
  navigateToBillingConfigurationFn,
}: SuccessDialogProps) => {
  return (
    <ActionDialog
      severity="success"
      title="Successfully created new billing task template"
      open={successDialogOpenState}
      onClose={() => {}} // Dont close on clickaway so that the dialog can remain and user must take action to avoid them accidentally creating multiple billing task templates
      renderActions={() => (
        <>
          <OSActionButton size="large" variant="contained" onClick={navigateToWorkflowBuilderFn}>
            Open in Workflow Builder
          </OSActionButton>
          <OSActionButton size="large" variant="contained" onClick={navigateToBillingConfigurationFn}>
            Configure Pricing
          </OSActionButton>
        </>
      )}
      dialogMessage={
        <p>
          You have successfully created a new billing task template. The task has been appended to the end of the
          workflow. You can go and adjust this in the workflow builder.
        </p>
      }
    />
  );
};
